import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import {useRecoilValue, useSetRecoilState} from "recoil";
import * as Renders from "../../../component/AdvancedFiltersRenders";
import AdvancedFilter from "../../../component/AdvancedFilter";
import { STATE_ACTIVE_FILTERS } from "../constants/ReportRecoil";
import {
    ID_FILTER_CREATION_DATE_FROM,
    ID_FILTER_CREATION_DATE_TO,
    ID_FILTER_PAYMENT_DATE_FROM,
    ID_FILTER_PAYMENT_DATE_TO,
    ID_FILTER_REPORT_NAME,
    ID_FILTER_STATUS, reportDefaultValues,
    TRANSACTION_STATUSES
} from "../constants/ReportConstants";
import {addFilter, getFilterValue, renderMultiSelect} from "../../../utils/FilterUtils";
import {useContext, useState} from "react";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";
import {ID_FILTER_DEALERS, ID_FILTER_LEGAL_ENTITIES} from "../constants/CardConstants";

function ReportFilters() {
    const { t } = useTranslation("finance", { keyPrefix: "payments.amex.transactions" });
    const filterDefs = useRecoilValue(STATE_ACTIVE_FILTERS);
    const setFilters = useSetRecoilState(STATE_ACTIVE_FILTERS);
    const { t: tg } = useTranslation("translation");

    const {
        globalData: { user },
    } = useContext(GlobalContext);

    const legalEntityOptions: any[] = user.legalEntities
        ?.filter((x: any) => x.name.toLowerCase() !== "all")
        .map((x: any) => ({
            value: x.name,
            label: tg(`legalEntities.${x.name}`),
        }));

    const dealerOptions: any[] = user.dealers
        ?.filter((x: any) => x.name.toLowerCase() !== "all")
        .map((x: any) => ({
            value: x.name,
            label: tg(`dealers.${x.name}`),
        }));
    const [dealers, setDealers] = useState<any[]>(dealerOptions);

    const schema = Yup.object().shape({
        [ID_FILTER_CREATION_DATE_FROM]: Yup.date().nullable().test("testFrom", t('filters.errors.invalid-creation-date-from'), (value, context) => {
            return !value || !context.parent.creationDateTo || (value <= context.parent.creationDateTo);
        }).test('date-range', t('filters.errors.invalid-creation-date-from'), function (value) {
            const { dateTo } = this.parent;
            return !value || !dateTo || value <= dateTo;
        }).typeError(t('filters.errors.invalid-creation-date-from')),
        [ID_FILTER_CREATION_DATE_TO]: Yup.date().nullable().test("testTo", t('filters.errors.invalid-creation-date-to'), (value, context) => {
            return !value || !context.parent.creationDateFrom || (value >= context.parent.creationDateFrom);
        }).test('date-range', t('filters.errors.invalid-creation-date-to'), function (value) {
            const { dateFrom } = this.parent;
            return !value || !dateFrom || value >= dateFrom;
        }).typeError(t('filters.errors.invalid-creation-date-to')), 
        [ID_FILTER_PAYMENT_DATE_FROM]: Yup.date().nullable().test("testPaymentFrom", t('filters.errors.invalid-payment-date-from'), (value, context) => {
            return !value || !context.parent.paymentDateTo || (value <= context.parent.paymentDateTo);
        }).test('date-range', t('filters.errors.invalid-payment-date-from'), function (value) {
            const { datePaymentTo } = this.parent;
            return !value || !datePaymentTo || value <= datePaymentTo;
        }).typeError(t('filters.errors.invalid-payment-date-from')),
        [ID_FILTER_PAYMENT_DATE_TO]: Yup.date().nullable().test("testPaymentTo", t('filters.errors.invalid-payment-date-to'), (value, context) => {
            return !value || !context.parent.paymentDateFrom || (value >= context.parent.paymentDateFrom);
        }).test('date-range', t('filters.errors.invalid-payment-date-to'), function (value) {
            const { datePaymentFrom } = this.parent;
            return !value || !datePaymentFrom || value >= datePaymentFrom;
        }).typeError(t('filters.errors.invalid-payment-date-to')),
        dealers: Yup.array(),
        legalEntities: Yup.array().min(1, tg('validation.selectAtLeastOne'))
    });

    const defaultValues = {
        [ID_FILTER_REPORT_NAME]: getFilterValue(ID_FILTER_REPORT_NAME, filterDefs) || "",
        [ID_FILTER_STATUS]: getFilterValue(ID_FILTER_STATUS, filterDefs) || [],
        [ID_FILTER_CREATION_DATE_FROM]: getFilterValue(ID_FILTER_CREATION_DATE_FROM, filterDefs) || null,
        [ID_FILTER_CREATION_DATE_TO]: getFilterValue(ID_FILTER_CREATION_DATE_TO, filterDefs) || null,
        [ID_FILTER_PAYMENT_DATE_FROM]: getFilterValue(ID_FILTER_PAYMENT_DATE_FROM, filterDefs) || null,
        [ID_FILTER_PAYMENT_DATE_TO]: getFilterValue(ID_FILTER_PAYMENT_DATE_TO, filterDefs) || null,
        legalEntities: legalEntityOptions.map((x: any) => x.value),
        dealers: [],
    };
    const fields = {
        main: {
            [ID_FILTER_REPORT_NAME]: Renders.renderTextField(
                ID_FILTER_REPORT_NAME,
                t("filters." + ID_FILTER_REPORT_NAME),

            ),
            [ID_FILTER_STATUS]: Renders.renderMultiSelect(
                ID_FILTER_STATUS,
                t("filters." + ID_FILTER_STATUS),
                TRANSACTION_STATUSES.map((x) => {
                    return { ...x, label: t(x.label) };
                })
            )
        },
        adv: {
            [ID_FILTER_CREATION_DATE_FROM]: Renders.renderDatePickerFrom(
                ID_FILTER_CREATION_DATE_FROM,
                t("filters." + ID_FILTER_CREATION_DATE_FROM)
            ),
            [ID_FILTER_CREATION_DATE_TO]: Renders.rendereDatePickerTo(
                ID_FILTER_CREATION_DATE_TO,
                t("filters." + ID_FILTER_CREATION_DATE_TO)
            ),
            [ID_FILTER_PAYMENT_DATE_FROM]: Renders.renderDatePickerFrom(
                ID_FILTER_PAYMENT_DATE_FROM,
                t("filters." + ID_FILTER_PAYMENT_DATE_FROM)
            ),
            [ID_FILTER_PAYMENT_DATE_TO]: Renders.rendereDatePickerTo(
                ID_FILTER_PAYMENT_DATE_TO,
                t("filters." + ID_FILTER_PAYMENT_DATE_TO)
            ),
            legalEntities: renderMultiSelect(
                tg("shared.legalEntities"),
                legalEntityOptions,
                "legalEntities",
                legalEntityOptions.length <= 1,
                (value: any, setValue: any) => {
                    setValue('dealers', []);
                    let dealers = user?.dealers
                        ?.filter(
                            (x: any) =>
                                value?.length > 0 &&
                                x.legalEntities.some((e: any) => value.includes(e.name)) &&
                                x.name.toLowerCase() !== "all"
                        )
                        .map((x: any) => {
                            return {
                                label: tg(`dealers.${x.name}`),
                                value: x.name,
                            };
                        });
                    setDealers(dealers);
                }
            ),
            dealers: renderMultiSelect(tg("shared.dealers"), dealers, "dealers", dealerOptions.length <= 1),

        }
    };

    const submit = (data: any) => {
        Object.keys(defaultValues)
            .forEach((param) => {
                addFilter(setFilters, param, data[param]);
            });

        addFilter(setFilters, ID_FILTER_LEGAL_ENTITIES, data.legalEntities);
        addFilter(setFilters, ID_FILTER_DEALERS, data.dealers);
    };

    const reset = () => {
        setFilters([]);
    };

    return (
        <AdvancedFilter
            schema={schema}
            mainFields={fields.main}
            fields={fields.adv}
            defaultValues={defaultValues}
            onSubmit={submit}
            onReset={reset}
            customDefaultValues={reportDefaultValues}

        />
    );
}

export default ReportFilters;
